import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://placeholder.supabase.co'
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'placeholder_key'
const supabaseServiceKey = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY || 'placeholder_service_key'

// Debug environment variables (only in development)
if (import.meta.env.DEV) {
  console.log('🔧 Supabase Config:', {
    url: supabaseUrl,
    anonKey: supabaseAnonKey ? `${supabaseAnonKey.substring(0, 10)}...` : 'missing',
    hasServiceKey: !!supabaseServiceKey,
    isProd: import.meta.env.PROD,
    isDev: import.meta.env.DEV
  })
}

// Only throw error in production
if (import.meta.env.PROD && (!supabaseUrl || !supabaseAnonKey || supabaseUrl === 'https://placeholder.supabase.co')) {
  throw new Error('Missing Supabase environment variables')
}

// Client-side Supabase client (uses anon key with RLS)
export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Test Supabase connection in development
if (import.meta.env.DEV) {
  console.log('🔧 Testing Supabase connection...')
  supabase.auth.getSession().then(({ data, error }) => {
    console.log('🔧 Supabase connection test:', { 
      hasSession: !!data.session, 
      error: error?.message 
    })
  }).catch(err => {
    console.error('❌ Supabase connection failed:', err)
  })
}

// Server-side Supabase client (uses service role key - for Edge Functions)
export const supabaseAdmin = createClient(supabaseUrl, supabaseServiceKey, {
  auth: {
    autoRefreshToken: false,
    persistSession: false
  }
})

// Database types
export interface Database {
  public: {
    Tables: {
      tasks: {
        Row: {
          id: string
          user_id: string
          title: string
          description: string | null
          status: 'pending' | 'completed'
          trello_card_id: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          user_id: string
          title: string
          description?: string | null
          status?: 'pending' | 'completed'
          trello_card_id?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          title?: string
          description?: string | null
          status?: 'pending' | 'completed'
          trello_card_id?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      card_history: {
        Row: {
          id: string
          user_id: string
          task_id: string
          trello_card_id: string
          trello_board_id: string
          trello_list_id: string
          card_url: string
          created_at: string
        }
        Insert: {
          id?: string
          user_id: string
          task_id: string
          trello_card_id: string
          trello_board_id: string
          trello_list_id: string
          card_url: string
          created_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          task_id?: string
          trello_card_id?: string
          trello_board_id?: string
          trello_list_id?: string
          card_url?: string
          created_at?: string
        }
      }
      meeting_notes: {
        Row: {
          id: string
          user_id: string
          content: string
          created_at: string
        }
        Insert: {
          id?: string
          user_id: string
          content: string
          created_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          content?: string
          created_at?: string
        }
      }
      user_trello_tokens: {
        Row: {
          id: string
          user_id: string
          trello_token: string
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          user_id: string
          trello_token: string
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          trello_token?: string
          created_at?: string
          updated_at?: string
        }
      }
      users: {
        Row: {
          id: string
          auth_user_id: string
          email: string
          full_name: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          auth_user_id: string
          email: string
          full_name?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          auth_user_id?: string
          email?: string
          full_name?: string | null
          created_at?: string
          updated_at?: string
        }
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      [_ in never]: never
    }
    Enums: {}
  }
}

export type Tables<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Row']
export type Enums<T extends keyof Database['public']['Enums']> = Database['public']['Enums'][T] 